/**
 * Core-100 CORE-BH / CORE-BG — read the autonomous-optimizer response by its REAL contract.
 *
 * useProvisionalOptimizer read `data.issuesDetected` as an array and `data.resolutionOptions`, but
 * autonomous-optimizer returns `issuesDetected` as a COUNT and the lists as `issues` / `options`. Both
 * reads were therefore always empty: after Add Job / Edit Jobs, a scope expansion was never detected on
 * the client and the resolution modal never opened. The server's issue shape also differs (`description`
 * not `message`; severities high / medium / low).
 *
 * This normalizer accepts both shapes (the legacy one stays readable) and carries the corrective
 * planners' suggestion-only menus (`issuePlans`).
 */
export interface ClientBoardIssue {
  id: string;
  type: string;
  severity: 'critical' | 'warning' | 'info';
  ticketId: string;
  message: string;
  context: Record<string, unknown>;
  detectedAt: string;
}

export interface DueOutPlanLite { kind: string; eligible: boolean; reasons: string[]; blockers: string[]; score: number; manualOnly: boolean }
export interface WaiterPlanLite { kind: string; reasons: string[]; blockers: string[]; manualOnly: boolean; suggestedTechId?: string }
export interface IssuePlansLite { dueOut?: DueOutPlanLite[]; waiter?: WaiterPlanLite }

const SEVERITY: Record<string, ClientBoardIssue['severity']> = {
  critical: 'critical', high: 'critical', warning: 'warning', medium: 'warning', info: 'info', low: 'info',
};

function toClientIssue(raw: Record<string, unknown>): ClientBoardIssue {
  const detected = raw.detectedAt;
  return {
    id: String(raw.id ?? ''),
    type: String(raw.type ?? ''),
    severity: SEVERITY[String(raw.severity ?? '')] ?? 'warning',
    ticketId: String(raw.ticketId ?? ''),
    message: String(raw.message ?? raw.description ?? ''),
    context: (raw.context && typeof raw.context === 'object' ? raw.context : {}) as Record<string, unknown>,
    detectedAt: detected instanceof Date ? detected.toISOString() : String(detected ?? ''),
  };
}

export function normalizeOptimizerResponse<TOption>(data: unknown): {
  issuesDetected: ClientBoardIssue[];
  resolutionOptions: TOption[];
  issuePlans: Record<string, IssuePlansLite>;
} {
  const d = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>;
  const rawIssues = Array.isArray(d.issuesDetected) ? d.issuesDetected : Array.isArray(d.issues) ? d.issues : [];
  const rawOptions = Array.isArray(d.resolutionOptions) ? d.resolutionOptions : Array.isArray(d.options) ? d.options : [];
  const plans = d.issuePlans && typeof d.issuePlans === 'object' ? d.issuePlans as Record<string, IssuePlansLite> : {};
  return {
    issuesDetected: (rawIssues as Array<Record<string, unknown>>).map(toClientIssue),
    resolutionOptions: rawOptions as TOption[],
    issuePlans: plans,
  };
}

/**
 * Core-100 R4 W5 (CORE-BH R / CORE-BG R): what the resolution modal lists for each promise in trouble — exactly the
 * rows `ResolutionOptionsModal` renders ("Promises affected"): the issue's words, the ELIGIBLE Due Out corrective
 * actions in the planner's order (labelled), and the waiter plan's first reason. Pure, so the served optimizer's
 * real response can be read through it on the real stack (tests/integration/coreR4W5RuntimeProof.test.ts).
 */
export interface PromisePlanRow { issueId: string; ticketId: string; type: string; message: string; actions: string[]; waiterNote: string | null }

export function promisePlanRows(
  issues: ReadonlyArray<Pick<ClientBoardIssue, 'id' | 'type' | 'ticketId' | 'message'>>,
  issuePlans: Record<string, IssuePlansLite>,
): PromisePlanRow[] {
  return issues
    .filter((i) => i.type === 'DUE_OUT_MISS' || i.type === 'WAITER_PROMISE_AT_RISK')
    .map((i) => {
      const plan = issuePlans[i.id];
      return {
        issueId: i.id,
        ticketId: i.ticketId,
        type: i.type,
        message: i.message,
        actions: (plan?.dueOut ?? []).filter((d) => d.eligible).map((d) => DUE_OUT_ACTION_LABELS[d.kind] ?? d.kind),
        waiterNote: plan?.waiter && plan.waiter.reasons.length > 0 ? plan.waiter.reasons[0] : null,
      };
    });
}

/** Human labels for the Due Out corrective action kinds (planner vocabulary). */
export const DUE_OUT_ACTION_LABELS: Record<string, string> = {
  reshuffle: 'Re-order this technician\'s work',
  lane_change: 'Move to another technician',
  bay_change: 'Move to a different bay',
  specialty_bay_correction: 'Correct the specialty bay',
  manual_merge_split_delay: 'Merge or split the work',
  parking_to_bay_acceleration: 'Bring the vehicle in from parking sooner',
  rollover_correction: 'Correct the carried-over work',
  authorized_due_out_edit: 'Agree a new time with the customer (manager)',
  breach_record: 'Record that the promise was missed',
};
